import { useCallback, useEffect } from "react";
import type { WithRouterProps } from "react-router";
import { withRouter } from "react-router";
import { push } from "react-router-redux";
import { useLocation } from "react-use";

import ActionCreator from "metabase/actions/containers/ActionCreator";
import CreateCollectionModal, {
  type CreateCollectionModalOwnProps,
} from "metabase/collections/containers/CreateCollectionModal";
import { useInitialCollectionId } from "metabase/collections/hooks";
import { CreateDashboardModal } from "metabase/common/CreateDashboard/CreateDashboardModal";
import { Modal } from "metabase/common/components/Modal";
import { UpgradeModal } from "metabase/common/components/upsells/components/UpgradeModal";
import { STATIC_LEGACY_EMBEDDING_TYPE } from "metabase/embedding/constants";
import {
  LegacyStaticEmbeddingModal,
  type LegacyStaticEmbeddingModalProps,
} from "metabase/embedding/embedding-iframe-sdk-setup/components/LegacyStaticEmbeddingModal";
import { SdkIframeEmbedSetupModal } from "metabase/embedding/embedding-iframe-sdk-setup/components/SdkIframeEmbedSetupModal";
import { PaletteShortcutsModal } from "metabase/palette/components/PaletteShortcutsModal/PaletteShortcutsModal";
import { useRegisterShortcut } from "metabase/palette/hooks/useRegisterShortcut";
import type { SdkIframeEmbedSetupModalProps } from "metabase/plugins";
import { useDispatch, useSelector } from "metabase/redux";
import { closeModal, setOpenModal } from "metabase/redux/ui";
import { getCurrentOpenModalState } from "metabase/selectors/ui";
import * as Urls from "metabase/urls";
import type { WritebackAction } from "metabase-types/api";

export const NewModals = withRouter((props: WithRouterProps) => {
  const { pathname } = useLocation();
  const { id: currentNewModalId, props: currentNewModalProps } = useSelector(
    getCurrentOpenModalState<CreateCollectionModalOwnProps>,
  );
  const dispatch = useDispatch();
  const collectionId = useInitialCollectionId(props) ?? undefined;

  const handleActionCreated = useCallback(
    (action: WritebackAction) => {
      const nextLocation = Urls.modelDetail({ id: action.model_id }, "actions");
      dispatch(push(nextLocation));
    },
    [dispatch],
  );

  const handleModalClose = useCallback(() => {
    dispatch(closeModal());
  }, [dispatch]);

  useEffect(() => {
    // Hide the modals on location change
    handleModalClose();
  }, [handleModalClose, pathname]);

  useRegisterShortcut(
    [
      {
        id: "shortcuts-modal",
        perform: () => {
          if (currentNewModalId) {
            handleModalClose();
          } else {
            dispatch(setOpenModal("help"));
          }
        },
      },
    ],
    [currentNewModalId],
  );

  switch (currentNewModalId) {
    case "collection": {
      const collectionProps = currentNewModalProps || null;

      return (
        <CreateCollectionModal
          onClose={handleModalClose}
          {...collectionProps}
          collectionId={collectionProps?.collectionId ?? collectionId}
        />
      );
    }

    case "dashboard":
      return (
        <CreateDashboardModal
          opened
          onClose={handleModalClose}
          collectionId={collectionId}
        />
      );
    case "action":
      return (
        <Modal wide onClose={handleModalClose} enableTransition={false}>
          <ActionCreator
            onClose={handleModalClose}
            onSubmit={handleActionCreated}
          />
        </Modal>
      );
    case "embed": {
      const props = currentNewModalProps as SdkIframeEmbedSetupModalProps;
      return (
        <SdkIframeEmbedSetupModal
          opened
          initialState={props?.initialState}
          onClose={handleModalClose}
        />
      );
    }
    case STATIC_LEGACY_EMBEDDING_TYPE: {
      const props = currentNewModalProps as LegacyStaticEmbeddingModalProps;

      return (
        <LegacyStaticEmbeddingModal
          experience={props?.experience}
          dashboardId={props?.dashboardId}
          questionId={props?.questionId}
          parentInitialState={props?.parentInitialState}
          onClose={handleModalClose}
        />
      );
    }
    case "upgrade":
      return <UpgradeModal opened onClose={handleModalClose} />;
    default:
      return (
        <PaletteShortcutsModal
          onClose={handleModalClose}
          open={currentNewModalId === "help"}
        />
      );
  }
});
